'use client';
import React from 'react';
import Link from 'next/link';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { useScrollAnimation } from '@/hooks/useScrollAnimation';
import NetruPartner from '@/components/NetruPartner';

const challenges = [
  {
    tag: '[01]',
    title: 'NIS2 Directive Obligations',
    desc: 'NIS2 significantly expands the scope of critical infrastructure security obligations across the EU. Risk management measures, incident reporting, and supply chain security are now mandatory, with significant penalties for non-compliance.',
  },
  {
    tag: '[02]',
    title: 'OT/ICS Security',
    desc: 'Operational technology and industrial control systems were not designed with cybersecurity in mind. Converging IT and OT environments create attack paths that traditional IT security programmes cannot address.',
  },
  {
    tag: '[03]',
    title: 'Resilience Under Attack',
    desc: 'Critical infrastructure is a primary target for nation-state actors and ransomware groups. Resilience planning, incident response, and recovery capabilities must be tested, not just documented.',
  },
  {
    tag: '[04]',
    title: 'Supply Chain Integrity',
    desc: 'Critical infrastructure operators depend on complex supply chains of hardware, software, and managed service providers. NIS2 mandates supply chain security measures, and the attack surface is enormous.',
  },
];

const services = [
  { label: 'NIS2 Compliance Programme', href: '/services/compliance', desc: 'Risk management measures, incident reporting frameworks, and supply chain security controls mapped to NIS2 obligations, built for critical infrastructure operators.' },
  { label: 'Threat Modelling for OT/ICS', href: '/services/threat-modelling', desc: 'Security architecture review for operational technology, SCADA systems, and IT/OT convergence points, before vulnerabilities reach critical systems.' },
  { label: 'Accelerator Programme', href: '/services/accelerator', desc: 'Rapid NIS2 readiness ahead of regulatory deadlines or incident reviews. Senior engineers embedded, doing the work at pace.' },
  { label: 'Bespoke Security Programme', href: '/services/security-programmes', desc: 'A security programme designed for your critical infrastructure environment, not a generic framework. Mapped to NIS2, ISO 27001, and sector-specific requirements.' },
  { label: 'IaC & Pipeline Security', href: '/services/iac-pipeline', desc: 'Secure infrastructure provisioning for critical systems. Policy-as-code enforcement, secrets detection, and supply chain security for critical infrastructure environments.' },
  { label: 'Security Focus Programme', href: '/services/security-focus', desc: 'Prioritised, auditor-aligned programme that cuts unnecessary work and focuses your team on controls that close real exposure in critical systems.' },
];

const outcomes = [
  { metric: 'NIS2', label: 'Risk management measures implemented and evidenced' },
  { metric: 'ISO 27001', label: 'Certification achieved for critical infrastructure environments' },
  { metric: 'OT/ICS', label: 'IT/OT convergence security architecture reviewed' },
  { metric: 'Supply Chain', label: 'Third-party risk registers built and maintained' },
];

const PARTNER_HEADING = 'We work where IT meets OT';
const PARTNER_BODY =
  'Our engineers secure SCADA, industrial control and the convergence points most consultancies will not touch, in estates that cannot simply be taken offline for a weekend. Netru handles the regulator, whichever framework you answer to.';

export default function CriticalInfrastructurePage() {
  useScrollAnimation();

  return (
    <div className="min-h-screen bg-[#050a0f] text-[#f5f5f8] noise-overlay-animated">
      <Navbar />

      {/* Hero */}
      <section className="relative pt-32 pb-20 lg:pt-40 lg:pb-28 overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="orb orb-blue" style={{ width: '600px', height: '600px', top: '-150px', left: '-100px', opacity: 0.3 }} />
          <div className="orb orb-teal" style={{ width: '400px', height: '400px', bottom: '0', right: '5%', opacity: 0.2 }} />
        </div>
        <div className="relative z-10 max-w-[1440px] mx-auto px-6 lg:px-12">
          <div className="flex items-center gap-3 mb-6 fade-in-up">
            <Link href="/" className="font-mono-brand text-[0.65rem] text-[#555] uppercase tracking-widest hover:text-[#80edfa] transition-colors">Home</Link>
            <span className="text-[#333]">/</span>
            <span className="font-mono-brand text-[0.65rem] text-[#555] uppercase tracking-widest">Industries</span>
            <span className="text-[#333]">/</span>
            <span className="font-mono-brand text-[0.65rem] text-[#80edfa] uppercase tracking-widest">Critical Infrastructure</span>
          </div>
          <div className="max-w-[900px]">
            <p className="font-mono-brand text-[0.7rem] text-[#80edfa] uppercase tracking-widest mb-4 fade-in-up" style={{ transitionDelay: '0.05s' }}>
              [Critical Infrastructure]
            </p>
            <h1 className="font-heading text-[clamp(2.5rem,6vw,4.5rem)] text-[#f5f5f8] font-medium leading-[1.05] mb-6 fade-in-up" style={{ transitionDelay: '0.1s' }}>
              NIS2, OT security,<br />
              <span className="text-[#80edfa]">and operational resilience.</span>
            </h1>
            <p className="text-[#aaa] text-lg leading-relaxed max-w-[680px] fade-in-up" style={{ transitionDelay: '0.15s' }}>
              Critical infrastructure operators face nation-state threats, NIS2 obligations, and the unique challenge of securing OT and ICS environments that were never designed for the modern threat landscape. TechSonar brings senior security engineers who understand both the regulatory requirements and the operational realities.
            </p>
          </div>
        </div>
      </section>

      {/* Outcomes strip */}
      <section className="border-t border-[#222] py-12 bg-[#0a0f14]">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {outcomes?.map((o, i) => (
              <div key={i} className="fade-in-up" style={{ transitionDelay: `${i * 0.07}s` }}>
                <p className="font-heading text-2xl text-[#80edfa] font-medium mb-2">{o?.metric}</p>
                <p className="text-xs text-[#555] leading-relaxed">{o?.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Challenges */}
      <section className="border-t border-[#222] py-20 lg:py-28">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <div className="mb-12 fade-in-up">
            <p className="font-mono-brand text-[0.65rem] text-[#555] uppercase tracking-widest mb-2">The challenge</p>
            <h2 className="font-heading text-[clamp(1.8rem,4vw,3rem)] text-[#f5f5f8] font-medium leading-tight max-w-[600px]">
              What makes critical infrastructure security different.
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-[#222]">
            {challenges?.map((c, i) => (
              <div key={i} className="bg-[#050a0f] p-8 lg:p-10 fade-in-up" style={{ transitionDelay: `${i * 0.07}s` }}>
                <p className="font-mono-brand text-[0.65rem] text-[#80edfa] uppercase tracking-widest mb-4">{c?.tag}</p>
                <h3 className="font-heading text-xl text-[#f5f5f8] font-medium mb-3">{c?.title}</h3>
                <p className="text-sm text-[#666] leading-relaxed">{c?.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="border-t border-[#222] py-20 lg:py-28 bg-[#0a0f14]">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <div className="mb-12 fade-in-up">
            <p className="font-mono-brand text-[0.65rem] text-[#555] uppercase tracking-widest mb-2">How we help</p>
            <h2 className="font-heading text-[clamp(1.8rem,4vw,3rem)] text-[#f5f5f8] font-medium leading-tight max-w-[600px]">
              Services built for critical infrastructure operators.
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-[#222]">
            {services?.map((s, i) => (
              <Link
                key={i}
                href={s?.href}
                className="group bg-[#0a0f14] p-8 hover:bg-[#111519] transition-colors duration-300 fade-in-up"
                style={{ transitionDelay: `${i * 0.07}s` }}
              >
                <div className="flex items-start justify-between mb-4">
                  <h3 className="font-heading text-lg text-[#f5f5f8] font-medium group-hover:text-[#80edfa] transition-colors pr-4">{s?.label}</h3>
                  <svg className="w-4 h-4 text-[#333] group-hover:text-[#80edfa] transition-colors flex-shrink-0 mt-1" viewBox="0 0 16 16" fill="none">
                    <path d="M3 13L13 3M13 3H6M13 3V10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
                <p className="text-sm text-[#555] leading-relaxed group-hover:text-[#888] transition-colors">{s?.desc}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="border-t border-[#222] py-20 lg:py-28">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <div className="max-w-[700px] fade-in-up">
            <p className="font-mono-brand text-[0.65rem] text-[#80edfa] uppercase tracking-widest mb-4">[Get started]</p>
            <h2 className="font-heading text-[clamp(2rem,4vw,3.5rem)] text-[#f5f5f8] font-medium leading-tight mb-6">
              NIS2 deadlines are not moving. Are you ready?
            </h2>
            <p className="text-[#aaa] text-lg leading-relaxed mb-8">
              Whether you are preparing for NIS2 compliance, securing OT/ICS environments, or building operational resilience, TechSonar brings the senior engineering expertise to get you there.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link href="/contact" className="btn-primary">Book a discovery call</Link>
              <Link href="/services/accelerator" className="px-6 py-3 border border-[#333] text-[#aaa] text-sm uppercase tracking-wide hover:border-[#80edfa] hover:text-[#80edfa] transition-colors font-sans">
                View Accelerator Programme
              </Link>
            </div>
          </div>
        </div>
      </section>

      <NetruPartner

        heading={PARTNER_HEADING}

        body={PARTNER_BODY}

      />


      <Footer />
    </div>
  );
}
